import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Alert,
  AlertTitle,
  IconButton,
  Box,
  Link,
  Typography,
  Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Build as MaintenanceIcon,
  Campaign as AnnouncementIcon
} from '@mui/icons-material';

// Component for animated text with fast opacity reveal - matching frontend scheme
const AnimatedText = ({ text, delay = 0, ...props }) => {
  const letters = text.split('');
  
  return (
    <motion.span
      initial="hidden"
      animate="visible"
      transition={{ delay, staggerChildren: 0.015 }}
      style={{ display: 'inline-block' }}
      {...props}
    >
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1 }
          }}
          transition={{
            duration: 0.2,
            ease: "easeOut"
          }}
          style={{ display: 'inline-block' }}
        >
          {letter === ' ' ? '\u00A0' : letter}
        </motion.span>
      ))}
    </motion.span>
  );
};

const DynamicBanner = ({ s3BucketUrl }) => {
  const [banners, setBanners] = useState([]);
  const [dismissedBanners, setDismissedBanners] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Default S3 URL - can be overridden via props
  const bannerJsonUrl = s3BucketUrl || process.env.REACT_APP_BANNER_JSON_URL || 
    'https://ckauth-banner-config.s3.us-east-1.amazonaws.com/banner-config.json';

  useEffect(() => {
    fetchBanners();
    // Refresh banners every 5 minutes
    const interval = setInterval(fetchBanners, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [bannerJsonUrl]);

  useEffect(() => {
    // Load dismissed banners from sessionStorage (cleared on page reload)
    const dismissed = sessionStorage.getItem('dismissedBanners');
    if (dismissed) {
      try {
        setDismissedBanners(new Set(JSON.parse(dismissed)));
      } catch (e) {
        console.error('Error loading dismissed banners:', e);
      }
    }
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await fetch(bannerJsonUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch banners:', response.status);
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      // Filter active banners based on date/time
      const now = new Date();
      const activeBanners = (data.banners || []).filter(banner => {
        if (!banner.enabled) return false;
        
        const startDate = banner.startDate ? new Date(banner.startDate) : null;
        const endDate = banner.endDate ? new Date(banner.endDate) : null;
        
        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;
        
        return true;
      });

      setBanners(activeBanners);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (bannerId) => {
    const newDismissed = new Set(dismissedBanners);
    newDismissed.add(bannerId);
    setDismissedBanners(newDismissed);
    // Use sessionStorage instead of localStorage - clears on page reload
    sessionStorage.setItem('dismissedBanners', JSON.stringify([...newDismissed]));
  };

  const getSeverityIcon = (type) => {
    switch (type) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'maintenance':
        return <MaintenanceIcon />;
      case 'announcement':
        return <AnnouncementIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (type) => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'maintenance':
        return 'warning';
      case 'announcement':
        return 'info';
      default:
        return 'info';
    }
  };

  if (loading || banners.length === 0) {
    return null;
  }

  // Show only non-dismissed banners
  const visibleBanners = banners.filter(banner => !dismissedBanners.has(banner.id));

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <Box sx={{ 
      width: '100%', 
      zIndex: 100,
      position: 'relative' 
    }}>
      <AnimatePresence mode="wait">
        {visibleBanners.map((banner, index) => (
          <motion.div
            key={banner.id}
            initial={{ opacity: 0, y: -30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.95 }}
            transition={{ 
              duration: 0.5,
              delay: index * 0.1,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            <Alert
              severity={getSeverityColor(banner.type)}
              icon={
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ 
                    duration: 0.5,
                    delay: index * 0.1 + 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  {getSeverityIcon(banner.type)}
                </motion.div>
              }
              action={
                banner.dismissible !== false && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      duration: 0.3,
                      delay: index * 0.1 + 0.3
                    }}
                  >
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => handleDismiss(banner.id)}
                      sx={{
                        padding: '4px',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.1)',
                          transform: 'scale(1.1)',
                          transition: 'all 0.2s ease',
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </motion.div>
                )
              }
              sx={{
                mb: 0,
                py: 0.75,
                px: 2,
                borderRadius: 0,
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                position: 'relative',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  width: '100%',
                  py: 0,
                },
                '& .MuiAlert-icon': {
                  padding: '4px 0',
                  marginRight: '8px',
                  fontSize: '20px',
                },
                '& .MuiAlert-action': {
                  padding: '0',
                  marginRight: '0',
                  marginLeft: '8px',
                },
              }}
            >
              {banner.title && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    duration: 0.4,
                    delay: index * 0.1 + 0.15,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <AlertTitle 
                    sx={{ 
                      fontWeight: 600, 
                      mb: banner.message ? 0.5 : 0,
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                    }}
                  >
                    <AnimatedText text={banner.title} delay={index * 0.1 + 0.2} />
                  </AlertTitle>
                </motion.div>
              )}
              {banner.message && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ 
                    duration: 0.4,
                    delay: index * 0.1 + 0.25,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: banner.link ? 0.5 : 0,
                      fontSize: '0.813rem',
                      lineHeight: 1.4,
                    }}
                  >
                    <AnimatedText text={banner.message} delay={index * 0.1 + 0.3} />
                  </Typography>
                </motion.div>
              )}
              {banner.link && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3,
                    delay: index * 0.1 + 0.35,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <Link
                    href={banner.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'inherit',
                      fontWeight: 600,
                      fontSize: '0.813rem',
                      textDecoration: 'underline',
                      display: 'inline-flex',
                      alignItems: 'center',
                      '&:hover': {
                        textDecoration: 'none',
                        transform: 'translateX(2px)',
                        transition: 'all 0.2s ease',
                      }
                    }}
                  >
                    <AnimatedText text={banner.link.text || 'Learn more'} delay={index * 0.1 + 0.4} />
                  </Link>
                </motion.div>
              )}
            </Alert>
          </motion.div>
        ))}
      </AnimatePresence>
    </Box>
  );
};

export default DynamicBanner;
